/**
 * The pieces an activity's page is made of: its frame — the hero over the body
 * and the sidebar — its rewards as chips, the "what you earn" panel and the
 * "what you'll learn" panel.
 *
 * Shared because the chapter list, the activity views and the gameplay view all
 * render them — which is why they sit here rather than in any one of those.
 */

import styles from './activity.module.css';
import { Label } from '../../../components/label';
import { Chip } from '../../../components/chip';
import { Card } from '../../../components/card';
import { Button } from '../../../components/button';
import { ActivityKindPill } from '../../shell/activity-kind-pill';
import { Hero } from './hero';
import type { ReactNode } from 'react';

import { extractSkillRewards } from '../../../api/_shared/activity-payload';

import { useTranslation } from 'react-i18next';

import type { ActivityKind } from '../../../api/_shared/types';
import type {
  StudentActivity,
} from '../../../api/student/campaigns/types';

/**
 * An activity's page: its hero — the picture, the kind, the title, and its
 * reward chips with any of the view's own after them — over its body, and
 * beside the body the sidebar: the reward card the view passes, with its
 * action in it, over what the activity teaches. Every activity view and every
 * state of the gameplay view draws this one frame; the gameplay view alone
 * wrote it out seven times.
 */
export function ActivityPage({ activity, chips, reward, children }: {
  activity: StudentActivity;
  /** The view's own chips, after the rewards: an article's reading time. */
  chips?: ReactNode;
  /** The reward card; null where nothing can complete the activity now. */
  reward: ReactNode;
  children: ReactNode;
}) {
  return (
    <article>
      <ActivityHero image={activity.imageUrl} kind={activity.type} title={activity.title}>
        <ActivityRewards activity={activity} />
        {chips}
      </ActivityHero>
      <div className={styles.actLayout}>
        {children}
        <aside className={styles.actSide}>
          {reward}
          <ActivityLearnCard payload={activity.payload} />
        </aside>
      </div>
    </article>
  );
}

/** The hero of an activity or a chapter's extra material (hero.tsx, its size): its picture, its kind, its title, and its chips beneath. */
export function ActivityHero({ image, kind, title, children }: {
  image: string | null | undefined;
  kind: ActivityKind;
  title: string;
  children: ReactNode;
}) {
  return (
    <Hero size="activity" image={image} badge={<ActivityKindPill sunken kind={kind} />} title={title}>
      <div className={styles.actHero__meta}>{children}</div>
    </Hero>
  );
}

function SkillRewards({ payload }: { payload: Record<string, unknown> | null | undefined }) {
  const { t } = useTranslation();
  const skills = extractSkillRewards(payload);
  if (skills.length === 0) return null;
  return (
    <>
      {skills.map((s) => (
        <Chip key={s.skillId} tone="skill">
          +{s.points} {t(`skills.${s.skillId}.name`, { defaultValue: s.skillId })}
        </Chip>
      ))}
    </>
  );
}

/**
 * What an activity pays, as chips: its XP, its KP and its skill points. Six
 * places wrote the first two by hand, and said KP in English where Spanish
 * and Portuguese call it PC; the words are the locales' now.
 */
export function ActivityRewards({ activity }: { activity: StudentActivity }) {
  const { t } = useTranslation();
  return (
    <>
      <Chip tone="xp">{t('student.campaign.rewardXp', { amount: activity.xpReward })}</Chip>
      <Chip tone="kp">{t('student.campaign.rewardKp', { amount: activity.kpReward })}</Chip>
      <SkillRewards payload={activity.payload} />
    </>
  );
}

/** The way a view completes its activity, as the reward card offers it: its words, and the click it takes or the form it submits. */
interface CompleteAction {
  words: string;
  onClick?: () => void;
  /** The id of the form its button submits, for a view whose completion is a form's (the quiz). */
  form?: string;
  /** The completion is under way. */
  busy?: boolean;
}

/**
 * The reward card, with the way to the activity's completion beside what it
 * pays: the view's action while it offers one; once the activity is done,
 * "You earned" over the chips, and ✓ Completed where the view offers no action
 * (a running session's way in stays); and, while its chapter is shut, why,
 * where the action would be. An action outranks the note: a running session
 * in a shut chapter is entered all the same (the launch reads no chapter), and
 * playing it completes the activity. Every activity view draws its sidebar's
 * card through it, the gameplay view's way in among the actions; a view draws
 * none where nothing can complete the activity any more.
 */
export function ActivityRewardCard({ activity, done = false, action = null, shutNote = null }: {
  activity: StudentActivity;
  /** The student has completed it, and earned what it lists. */
  done?: boolean;
  action?: CompleteAction | null;
  /** Why it cannot be completed yet, while its chapter is shut (chapter-shut.ts). */
  shutNote?: string | null;
}) {
  const { t } = useTranslation();
  return (
    <Card framed className={`${styles.studentAct__reward}`}>
      <Label>{t('student.campaign.yourReward')}</Label>
      <p className={`${styles.studentAct__rewardHint}`}>{t(done ? 'student.campaign.youEarned' : 'student.campaign.completeToEarn')}</p>
      <div className={`${styles.studentAct__rewardChips}`}>
        <ActivityRewards activity={activity} />
      </div>
      {action ? (
        <Button block className={styles.studentAct__rewardAction} type={action.form ? 'submit' : 'button'} form={action.form} onClick={action.onClick} disabled={action.busy}>
          {action.words}
        </Button>
      ) : done ? (
        <Button block className={styles.studentAct__rewardAction} disabled>✓ {t('student.campaign.completed')}</Button>
      ) : shutNote ? (
        <p className={`${styles.studentAct__shutNote}`}>{shutNote}</p>
      ) : null}
    </Card>
  );
}

/** Standard activity sidebar — "What you'll learn" card (hidden when empty). */
function ActivityLearnCard({ payload }: { payload: Record<string, unknown> }) {
  const { t } = useTranslation();
  const points = (payload as { learningPoints?: string[] }).learningPoints ?? [];
  if (points.length === 0) return null;
  return (
    <Card framed className={`${styles.studentAct__learn}`}>
      <h3>{t('student.campaign.whatYoullLearn')}</h3>
      <ul>
        {points.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>
    </Card>
  );
}

